// Camera scanning in a browser engine (plain web and Electron): getUserMedia + ZXing on
// video frames. ⚠️ Performance on the target all-in-one PCs is still to be confirmed (plan §9.6).

import { decodeImage } from './decode.js';
import { delay, withTimeout } from './timeout.js';
import {
  DEVICE_TIMEOUT_MS,
  ScanError,
  type ScanOptions,
  type ScanRead,
  type Scanner,
} from './types.js';

/** Time between decode attempts. */
const FRAME_INTERVAL_MS = 120;
/** In continuous mode, the same code is reported again only after this pause. */
const REPEAT_AFTER_MS = 1_500;
/** Frames are scaled down to at most this width before decoding. */
const MAX_FRAME_WIDTH = 1_280;

function cameraError(error: unknown): ScanError {
  if (error instanceof ScanError) return error;
  const name = error instanceof DOMException || error instanceof Error ? error.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return new ScanError(
        'permission-denied',
        'Camera access was blocked. Allow the camera and try again.',
      );
    case 'NotFoundError':
    case 'OverconstrainedError':
      return new ScanError('no-camera', 'No camera found on this device.');
    case 'NotReadableError':
      return new ScanError('error', 'The camera is in use by another app.');
    default:
      return new ScanError('error', error instanceof Error ? error.message : 'The camera failed.');
  }
}

async function openCamera(timeoutMs: number): Promise<MediaStream> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    throw new ScanError('unsupported', 'This device has no camera access.');
  }
  let late: Promise<MediaStream> | undefined;
  const stream = await withTimeout(
    () => {
      late = navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      return late;
    },
    timeoutMs,
    () => undefined,
  ).catch((error: unknown) => {
    throw cameraError(error);
  });
  if (!stream) {
    // If the camera turns up after the timeout, switch it straight off again.
    void late?.then((s) => s.getTracks().forEach((t) => t.stop())).catch(() => undefined);
    throw new ScanError(
      'timeout',
      `The camera did not start within ${Math.round(timeoutMs / 1000)} seconds.`,
    );
  }
  return stream;
}

async function* scanCamera(opts: ScanOptions = {}): AsyncGenerator<ScanRead> {
  const { signal, once = true, preview, startTimeoutMs = DEVICE_TIMEOUT_MS } = opts;
  if (signal?.aborted) return;
  const stream = await openCamera(startTimeoutMs);
  const video = preview ?? document.createElement('video');
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });
  const lastSeen = new Map<string, number>();
  try {
    if (!context) throw new ScanError('unsupported', 'This device cannot read camera frames.');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    await video.play().catch((error: unknown) => {
      throw cameraError(error);
    });

    while (!signal?.aborted) {
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0) {
        const scale = Math.min(1, MAX_FRAME_WIDTH / video.videoWidth);
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const frame = context.getImageData(0, 0, canvas.width, canvas.height);
        for (const read of await decodeImage(frame, 'camera')) {
          const now = Date.now();
          const previous = lastSeen.get(read.text);
          lastSeen.set(read.text, now);
          if (previous !== undefined && now - previous < REPEAT_AFTER_MS) continue;
          yield read;
          if (once) return;
        }
      }
      try {
        await delay(FRAME_INTERVAL_MS, signal);
      } catch {
        return; // aborted
      }
    }
  } finally {
    for (const track of stream.getTracks()) track.stop();
    video.srcObject = null;
  }
}

export function createCameraScanner(): Scanner {
  return { scan: (opts) => scanCamera(opts) };
}
